import React, { useCallback, useMemo } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { CommonButton } from '../../../../components/common/Forms/Button';
import { ConfirmDialog } from '../../../../components/Popup/ConfirmDialog/ConfirmDialog';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useMutateMassCancelAppointment } from '../../../../query/appointments';
import {
  formatDateOnlyISO,
  formatDateOnlySite,
  parseIsoDate,
  parseTimeInHHmmToSecondsOfDay,
} from '../../../../utils/dateUtil';
import { successToast } from '../../../../utils/toast';
import { MassCancelFormValues } from './StaffMassCancelHooks';

type Props = {
  formValues: MassCancelFormValues;
  onClose: () => void;
};

export const StaffMassCancelConfirmDialog = ({
  formValues,
  onClose,
}: Props) => {
  const { t } = useCommonTranslation();
  const { mutate: massCancelAppointment, isLoading: savingMassCancel } =
    useMutateMassCancelAppointment({
      onSuccess: () => {
        successToast(t('mass-cancel-success'));
        onClose();
      },
    });

  const dialogTitle = useMemo(() => {
    const { date, toDate, fromTime, toTime, fullDay } = formValues;
    const isRange = Boolean(toDate) && toDate !== date;
    const key = isRange
      ? fullDay
        ? 'confirm-mass-cancel-range-full-day'
        : 'confirm-mass-cancel-range'
      : fullDay
      ? 'confirm-mass-cancel-full-day'
      : 'confirm-mass-cancel';
    return t(key, {
      date: formatDateOnlySite(parseIsoDate(date)),
      toDate: toDate ? formatDateOnlySite(parseIsoDate(toDate)) : undefined,
      fromTime,
      toTime,
    });
  }, [formValues, t]);

  const handleConfirm = useCallback(() => {
    const {
      date: dateString,
      toDate: toDateString,
      fromTime: fromTimeString,
      toTime: toTimeString,
      clearApptTemplate,
      fullDay,
    } = formValues;
    const date = parseIsoDate(dateString);
    const fromSeconds = fullDay
      ? undefined
      : parseTimeInHHmmToSecondsOfDay(fromTimeString);
    const toSeconds = fullDay
      ? undefined
      : parseTimeInHHmmToSecondsOfDay(toTimeString);
    massCancelAppointment({
      date: formatDateOnlyISO(date),
      to_date: toDateString
        ? formatDateOnlyISO(parseIsoDate(toDateString))
        : undefined,
      from_seconds: fromSeconds,
      to_seconds: toSeconds,
      clear_appt_template: clearApptTemplate,
    });
  }, [formValues, massCancelAppointment]);

  return (
    <ConfirmDialog
      title={dialogTitle}
      icon={<FaExclamationTriangle />}
      status="delete"
      actionsTopMargin
      handleClose={onClose}
      isLoading={savingMassCancel}
      actions={[
        <CommonButton
          key={'confirm_true'}
          variant="danger"
          onClick={handleConfirm}
        >
          {t('yes')}
        </CommonButton>,
        <CommonButton
          key={'confirm_false'}
          variant="secondary"
          onClick={onClose}
        >
          {t('no')}
        </CommonButton>,
      ]}
    />
  );
};
